import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import Breadcrumb from '../components/Breadcrumb';
import ErrorComponent from '../routes/-errorComponent';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  pathname?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const DefaultErrorFallback = ({ error, onReset }: { error: Error; onReset?: () => void }) => (
  <>
    <ErrorComponent error={error} onReset={onReset} />
  </>
);

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.pathname !== this.props.pathname) {
      this.setState({ hasError: false, error: null });
    }
  }

  clearError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const fallbackError = this.state.error ?? new Error('Something went wrong');
      return <DefaultErrorFallback error={fallbackError} onReset={this.clearError} />;
    }
    return this.props.children;
  }
}
